import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx-js-style';
import { Box, Typography, TextField, Button, IconButton, Menu, MenuItem, Portal, Chip, CircularProgress } from '@mui/material';
import { RefreshCw, Plus, Download, BookOpen, TrendingDown, Inbox, Radio, User, UserCheck, Send, Zap, CheckCircle2, AlertCircle, MailX } from 'lucide-react';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ApiCall } from '../../helper/axios';
import { useDispatch, useSelector } from 'react-redux';
import { manageTrackerList, fetchTrackerDetails, sendTrackerForApproval, clearCurrentListDetails, fetchTrackerLists } from '../../redux/affiliateTrackerSlice';
import { selectEntityType } from '../../redux/authSlice';
import { setActiveModuleKey } from '../../redux/userDataSlice';
import { useLayout } from '../../contexts/LayoutContext';
import { useAccountOverview } from '../../context/AccountOverviewContext';
import PageContainer from '../../common_components/PageContainer';
import useToast from '../../common_components/Toast/useToast';
import { ROUTES } from '../../routes/routes.constants';
import CreatePublisherDrawer from './components/CreatePublisherDrawer/CreatePublisherDrawer';
import SearchKeywordDrawer from './components/SearchKeywordDrawer/SearchKeywordDrawer';
import EditTrackerDrawer from './components/EditTrackerDrawer';
import NotesModal from '../../common_components/NotesModal';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
import LastUpdatedInfo from '../../common_components/LastUpdatedInfo';
import MetricCard from '../../common_components/MetricCard';
import CommonTable from '../../common_components/CommonTable';
import Pagination from '../../common_components/Pagination';
import IOSSwitch from '../../common_components/IOSSwitch';
import { Dropdown, ExportButton } from '../../common_components/FormInputs';
import { FloatingSaveBar } from '../../common_components/FloatingSaveBar';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import { useOptedOutCheck } from '../../hooks/useOptedOutCheck';
import AssignToListModal from './components/AssignToListModal';
export default function AddOutreachTracker({ viewOnly = false }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { id: urlTrackerId } = useParams();
    const queryTrackerId = searchParams.get('id') || searchParams.get('trackerId') || '';
    const dispatch = useDispatch();
    const toast = useToast();
    const { setIsSidebarExpanded } = useLayout();
    const { selectedClientId } = useAccountOverview();
    const reduxClientName = useSelector((state) => state.accountOverview.clientName);
    const token = useSelector((state) => state.auth.token);
    const entityType = useSelector(selectEntityType);
    const isClient = entityType === 'client';
    const permissions = useSelector((state) => state.auth.permissions);
    const affiliateActions = permissions?.affiliate_account_performance?.actions || [];
    const hasManageTrackersAccess = affiliateActions.includes('Manage Trackers');
    const hasClientApprovalAccess = affiliateActions.includes('Client Approval Access');
    const trackerId = urlTrackerId || queryTrackerId || location.state?.trackerId || '';
    // Form State
    const [sessionTrackerId, setSessionTrackerId] = useState(trackerId);
    const activeTrackerId = trackerId || sessionTrackerId;
    // Redux Selectors and Fetch details
    const { currentListDetails = [], currentTrackerInfo, lists = [], loading } = useSelector((state) => state.affiliateTracker);
    const matchedTracker = useMemo(() => {
        return lists.find(l => l.id === activeTrackerId);
    }, [lists, activeTrackerId]);
    const buildDefaultName = (name) => {
        const today = new Date();
        const dd = String(today.getDate()).padStart(2, '0');
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const yyyy = today.getFullYear();
        return `${name || 'Client'} Outreach Tracker ${dd}.${mm}.${yyyy}`;
    };
    const formatEnrollmentDate = (dateVal) => {
        if (!dateVal)
            return '';
        try {
            const d = new Date(dateVal);
            if (isNaN(d.getTime()))
                return String(dateVal);
            return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
        }
        catch {
            return String(dateVal);
        }
    };
    const [trackerListName, setTrackerListName] = useState(() => location.state?.trackerListName || '');
    const userEditedName = useRef(!!location.state?.trackerListName);
    const displayClientName = useMemo(() => {
        const fromState = location.state?.clientName;
        if (fromState)
            return fromState;
        const fromCurrentTracker = currentTrackerInfo?.client_id?.name;
        if (fromCurrentTracker)
            return fromCurrentTracker;
        const fromMatched = matchedTracker?.client_id?.name;
        if (fromMatched)
            return fromMatched;
        const fromFirstRow = currentListDetails[0]?.client_id?.name;
        if (fromFirstRow)
            return fromFirstRow;
        if (reduxClientName)
            return reduxClientName;
        // Fallback: extract client name from tracker list name if pattern matches "Client Name Outreach Tracker ..."
        if (trackerListName) {
            const match = trackerListName.match(/^(.*?)\s+Outreach\s+Tracker/i);
            if (match && match[1] && match[1].toLowerCase() !== 'general' && match[1].toLowerCase() !== 'client') {
                return match[1].trim();
            }
        }
        return '';
    }, [location.state, currentTrackerInfo, matchedTracker, currentListDetails, reduxClientName, trackerListName]);
    // Tracker name inline edit
    const [editingTrackerName, setEditingTrackerName] = useState(false);
    const [tempTrackerName, setTempTrackerName] = useState('');
    const [savingTrackerName, setSavingTrackerName] = useState(false);
    // Sync activeTrackerId to URL search params so page refresh preserves tracker data
    useEffect(() => {
        if (activeTrackerId && searchParams.get('id') !== activeTrackerId) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('id', activeTrackerId);
                return next;
            }, { replace: true });
        }
    }, [activeTrackerId, searchParams, setSearchParams]);
    useEffect(() => {
        dispatch(setActiveModuleKey('affiliate_account_performance'));
        return () => {
            dispatch(setActiveModuleKey(null));
        };
    }, [dispatch]);
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    useEffect(() => {
        if (lists.length === 0) {
            dispatch(fetchTrackerLists());
        }
    }, [dispatch, lists.length]);
    // Table Filter and Pagination States
    const [contactNotAddedFilter, setContactNotAddedFilter] = useState(false);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('All');
    const [selectedKeywordFilter, setSelectedKeywordFilter] = useState('All');
    const [selectedDomainFilter, setSelectedDomainFilter] = useState('All');
    const [selectedMulticontactFilter, setSelectedMulticontactFilter] = useState('All');
    const [page, setPage] = useState(1);
    useEffect(() => {
        if (activeTrackerId) {
            dispatch(fetchTrackerDetails({ id: activeTrackerId, noContact: contactNotAddedFilter }));
        }
        else {
            dispatch(clearCurrentListDetails());
        }
    }, [dispatch, activeTrackerId, contactNotAddedFilter]);
    // Sync tracker name from fetched tracker contacts
    useEffect(() => {
        if (currentListDetails.length > 0) {
            const first = currentListDetails[0];
            if (first?.tracker_list && (!userEditedName.current || !trackerListName)) {
                setTrackerListName(first.tracker_list);
            }
        }
    }, [currentListDetails]);
    useEffect(() => {
        if (activeTrackerId && lists.length > 0) {
            const match = lists.find(l => l.id === activeTrackerId);
            if (match?.name && (!userEditedName.current || !trackerListName)) {
                setTrackerListName(match.name);
            }
        }
    }, [activeTrackerId, lists]);
    const [selectedRowIds, setSelectedRowIds] = useState(new Set());
    const itemsPerPage = 10;
    // Opted-out check
    const checkOptedOut = useOptedOutCheck();
    const [optedOutWarnings, setOptedOutWarnings] = useState(new Set());
    // Inline Edit States
    const [editingCell, setEditingCell] = useState(null);
    const [tempValue, setTempValue] = useState('');
    // Floating Save Bar and Bulk Actions State
    const [pendingEdits, setPendingEdits] = useState({});
    const [savingPending, setSavingPending] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [sendingApproval, setSendingApproval] = useState(false);
    const [autoFetchLoading, setAutoFetchLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [stagedStatus, setStagedStatus] = useState('');
    // Multicontact Edit Drawer States
    const [editTrackerDrawerOpen, setEditTrackerDrawerOpen] = useState(false);
    const [selectedTrackerRow, setSelectedTrackerRow] = useState(null);
    // Notes Dialog States
    const [notesOpen, setNotesOpen] = useState(false);
    const [selectedNotesRow, setSelectedNotesRow] = useState(null);
    const [notesValue, setNotesValue] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    // Dynamic selected row article names for bulk list details
    const selectedArticleNames = useMemo(() => {
        return Array.from(selectedRowIds).map(rowId => {
            const row = currentListDetails.find(r => r.id === rowId);
            return row ? (row.article || row.domain || 'Unnamed Site') : 'Unnamed Site';
        });
    }, [selectedRowIds, currentListDetails]);
    // Handlers
    const handleSaveInline = (row, field, value) => {
        setEditingCell(null);
        const trimmedValue = value.trim();
        if ((row[field] || '') === trimmedValue) {
            setPendingEdits(prev => {
                const rowEdits = { ...prev[row.id] };
                delete rowEdits[field];
                if (Object.keys(rowEdits).length === 0) {
                    const next = { ...prev };
                    delete next[row.id];
                    return next;
                }
                return { ...prev, [row.id]: rowEdits };
            });
            return;
        }
        setPendingEdits(prev => ({
            ...prev,
            [row.id]: {
                ...prev[row.id],
                [field]: trimmedValue
            }
        }));
    };
    // Check if active editing cell has changes compared to original row
    const hasActiveCellChange = useMemo(() => {
        if (!editingCell)
            return false;
        const activeRow = currentListDetails.find(r => r.id === editingCell.rowId);
        if (!activeRow)
            return false;
        return (activeRow[editingCell.field] || '') !== tempValue.trim();
    }, [editingCell, tempValue, currentListDetails]);
    const totalUnsavedCount = useMemo(() => {
        const rowKeys = new Set(Object.keys(pendingEdits));
        if (hasActiveCellChange && editingCell) {
            rowKeys.add(editingCell.rowId);
        }
        return rowKeys.size;
    }, [pendingEdits, hasActiveCellChange, editingCell]);
    // Save all pending inline edits
    const handleSaveAllPendingEdits = async () => {
        setSavingPending(true);
        try {
            // Merge currently active editing cell into pendingEdits if any
            let currentEdits = { ...pendingEdits };
            if (editingCell) {
                const activeRow = currentListDetails.find(r => r.id === editingCell.rowId);
                const trimmedValue = tempValue.trim();
                if (activeRow && (activeRow[editingCell.field] || '') !== trimmedValue) {
                    currentEdits = {
                        ...currentEdits,
                        [editingCell.rowId]: {
                            ...(currentEdits[editingCell.rowId] || {}),
                            [editingCell.field]: trimmedValue
                        }
                    };
                }
                setEditingCell(null);
            }
            if (Object.keys(currentEdits).length === 0) {
                setSavingPending(false);
                return;
            }
            const promises = Object.entries(currentEdits).map(async ([rowId, edits]) => {
                const originalRow = currentListDetails.find(r => r.id === rowId);
                if (!originalRow)
                    return;
                const siblings = currentListDetails.filter(c => c.parent_id && String(c.parent_id) === String(rowId));
                const payload = {
                    id: rowId,
                    data: {
                        contacts: [
                            {
                                first_name: edits.first_name !== undefined ? edits.first_name : originalRow.first_name,
                                last_name: edits.last_name !== undefined ? edits.last_name : originalRow.last_name,
                                primary_email: edits.primary_email !== undefined ? edits.primary_email : originalRow.primary_email,
                                secondary_email: originalRow.secondary_email || '',
                                job_title: originalRow.job_title || '',
                                status: originalRow.status || 'Pending'
                            },
                            ...siblings.map(sib => ({
                                first_name: sib.first_name || '',
                                last_name: sib.last_name || '',
                                primary_email: sib.primary_email || '',
                                secondary_email: sib.secondary_email || '',
                                job_title: sib.job_title || '',
                                status: sib.status || 'Pending'
                            }))
                        ]
                    }
                };
                await dispatch(manageTrackerList({ action: 'update', payload })).unwrap();
            });
            await Promise.all(promises);
            toast.success('Changes saved successfully.');
            setPendingEdits({});
            setOptedOutWarnings(new Set());
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err || 'Failed to save changes.');
        }
        finally {
            setSavingPending(false);
        }
    };
    // Bulk status update for selected rows
    const handleBulkStatusChange = async (status) => {
        setSavingPending(true);
        try {
            const validStatuses = ['Pending', 'Approved', 'Rejected'];
            const sanitizeStatus = (s) => validStatuses.includes(s) ? s : 'Pending';
            const promises = Array.from(selectedRowIds).map(async (rowId) => {
                const originalRow = currentListDetails.find(r => r.id === rowId);
                if (!originalRow)
                    return;
                const siblings = currentListDetails.filter(c => c.parent_id && String(c.parent_id) === String(rowId));
                const payload = {
                    id: rowId,
                    data: {
                        contacts: [
                            {
                                first_name: originalRow.first_name || '',
                                last_name: originalRow.last_name || '',
                                primary_email: originalRow.primary_email || '',
                                secondary_email: originalRow.secondary_email || '',
                                job_title: originalRow.job_title || '',
                                status: sanitizeStatus(status)
                            },
                            ...siblings.map(sib => ({
                                first_name: sib.first_name || '',
                                last_name: sib.last_name || '',
                                primary_email: sib.primary_email || '',
                                secondary_email: sib.secondary_email || '',
                                job_title: sib.job_title || '',
                                status: sanitizeStatus(sib.status || 'Pending')
                            }))
                        ]
                    }
                };
                await dispatch(manageTrackerList({ action: 'update', payload })).unwrap();
            });
            await Promise.all(promises);
            toast.success(`Updated status to ${status} for ${selectedRowIds.size} rows successfully!`);
            setSelectedRowIds(new Set());
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err || 'Failed to update status.');
        }
        finally {
            setSavingPending(false);
        }
    };
    // Bulk delete for selected rows
    const handleBulkDelete = async () => {
        setSavingPending(true);
        try {
            const promises = Array.from(selectedRowIds).map(async (rowId) => {
                await dispatch(manageTrackerList({ action: 'delete', payload: { id: rowId } })).unwrap();
            });
            await Promise.all(promises);
            toast.success(`Deleted ${selectedRowIds.size} outreach trackers successfully!`);
            setSelectedRowIds(new Set());
            setDeleteModalOpen(false);
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err || 'Failed to delete selected rows.');
        }
        finally {
            setSavingPending(false);
        }
    };
    const isBounced = (row) => {
        if (!row?.bounce_reason)
            return false;
        const domain = (row.domain || '').trim().toLowerCase();
        const inPositive = (row.positive_lead || '').split(';').map((s) => s.trim().toLowerCase()).includes(domain);
        const inNegative = (row.negative_lead || '').split(';').map((s) => s.trim().toLowerCase()).includes(domain);
        const inNeutral = (row.neutral || '').split(';').map((s) => s.trim().toLowerCase()).includes(domain);
        return !inPositive && !inNegative && !inNeutral;
    };
    const isIneligible = (c) => c?.sequences_is_enrolled === 'true' ||
        c?.sequence_enrolled === 'true' ||
        isBounced(c);
    // Only Approved contacts may be pushed to HubSpot
    const isApproved = (c) => String(c?.status || '').trim().toLowerCase() === 'approved';
    // Bulk HubSpot Sync for selected rows — calls real HubSpot API
    const handleBulkHubspotSync = async () => {
        const selectedIds = [];
        selectedRowIds.forEach(id => {
            selectedIds.push(id);
            const children = currentListDetails.filter(c => c.parent_id === id);
            children.forEach(ch => {
                if (ch.primary_email?.trim() && !selectedIds.includes(ch.id)) {
                    selectedIds.push(ch.id);
                }
            });
        });
        const validIds = selectedIds.filter(id => {
            const row = currentListDetails.find(r => r.id === id);
            return row?.primary_email?.trim() && isApproved(row) && !isBounced(row) && !isIneligible(row);
        });
        if (validIds.length === 0) {
            const hasUnapproved = selectedIds.some(id => {
                const row = currentListDetails.find(r => r.id === id);
                return row && !isApproved(row);
            });
            toast.error(hasUnapproved
                ? 'Only Approved contacts can be synced to HubSpot. Approve the selected contacts first.'
                : 'No eligible contacts with valid email to sync.');
            return;
        }
        setSavingPending(true);
        try {
            const res = await ApiCall('POST', '/api/affiliate-tracker/hubspot-sync', { type: 'direct_sync', contact_ids: validIds }, { Authorization: token });
            const data = res?.data;
            if (data?.success) {
                const synced = data.data?.results?.filter((r) => r.status === 'Synced').length ?? 0;
                const failed = data.data?.results?.filter((r) => r.status === 'Failed').length ?? 0;
                toast.success(`HubSpot sync complete — ${synced} synced${failed ? `, ${failed} failed` : ''}.`);
                setSelectedRowIds(new Set());
                refreshTrackerData();
            }
            else {
                toast.error(data?.message || 'HubSpot sync failed.');
            }
        }
        catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to sync to HubSpot.');
        }
        finally {
            setSavingPending(false);
        }
    };
    const handleOpenEditDrawer = (row) => {
        setSelectedTrackerRow(row);
        setEditTrackerDrawerOpen(true);
    };
    const handleOpenNotes = (row) => {
        setSelectedNotesRow(row);
        setNotesValue(row.notes || '');
        setNotesOpen(true);
    };
    const handleSaveNotes = async (newNotes) => {
        if (!selectedNotesRow)
            return;
        setNotesSaving(true);
        try {
            const payload = {
                id: selectedNotesRow.id,
                data: {
                    notes: newNotes.trim()
                }
            };
            await dispatch(manageTrackerList({ action: 'update', payload })).unwrap();
            toast.success('Notes updated successfully.');
            setNotesOpen(false);
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err || 'Failed to update notes.');
            throw err;
        }
        finally {
            setNotesSaving(false);
        }
    };
    const refreshTrackerData = () => {
        dispatch(fetchTrackerDetails({ id: trackerId || sessionTrackerId, noContact: contactNotAddedFilter }));
    };
    const handleContactSync = async () => {
        setHubspotAnchor(null);
        // sync all contacts of this tracker that have a primary email
        const ids = currentListDetails
            .filter(r => r.primary_email?.trim() && isApproved(r))
            .map(r => r.id);
        if (ids.length === 0) {
            toast.info('No Approved contacts with an email found to sync. Only Approved contacts can be synced to HubSpot.');
            refreshTrackerData();
            return;
        }
        setSyncLoading(true);
        try {
            const res = await ApiCall('POST', '/api/affiliate-tracker/hubspot-sync', { type: 'direct_sync', contact_ids: ids }, { Authorization: token });
            const data = res?.data;
            if (data?.success) {
                toast.success(data?.message || 'HubSpot sync completed successfully.');
            }
            else {
                toast.error(data?.message || 'HubSpot sync failed.');
            }
        }
        catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'HubSpot sync failed.');
        }
        finally {
            try {
                await dispatch(fetchTrackerDetails({ id: trackerId || sessionTrackerId, noContact: contactNotAddedFilter })).unwrap();
            }
            catch { }
            setSyncLoading(false);
        }
    };
    const handleSaveTrackerName = async () => {
        const finalId = trackerId || sessionTrackerId;
        const trimmed = tempTrackerName.trim();
        if (!trimmed || trimmed === trackerListName) {
            setEditingTrackerName(false);
            return;
        }
        if (!finalId) {
            setTrackerListName(trimmed);
            userEditedName.current = true;
            setEditingTrackerName(false);
            return;
        }
        setSavingTrackerName(true);
        try {
            await dispatch(manageTrackerList({
                action: 'update',
                payload: { id: finalId, isList: true, data: { name: trimmed } }
            })).unwrap();
            setTrackerListName(trimmed);
            userEditedName.current = true;
            setEditingTrackerName(false);
            toast.success('Tracker name updated successfully.');
            dispatch(fetchTrackerDetails({ id: finalId, noContact: contactNotAddedFilter }));
            dispatch(fetchTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to update tracker name.');
        }
        finally {
            setSavingTrackerName(false);
        }
    };
    const handleCancelTrackerName = () => {
        setTempTrackerName(trackerListName);
        setEditingTrackerName(false);
    };
    const allContactsApproved = currentListDetails.length > 0 && currentListDetails.every(c => c.status === 'Approved');
    const handleBulkAutoFetch = async () => {
        if (currentListDetails.length === 0) {
            toast.warning('No records found in the current list.');
            return;
        }
        setAutoFetchLoading(true);
        try {
            const res = await ApiCall('POST', '/api/affiliate-tracker/bulk-fetch-contact', { records: currentListDetails }, { Authorization: token });
            const data = res?.data;
            if (data?.success) {
                const { updated_count, failed_count } = data.data || {};
                toast.success(`Auto-fetch completed! ${updated_count} records updated, ${failed_count} failed.`);
                refreshTrackerData();
            }
            else {
                toast.error(data?.message || 'Failed to auto-fetch contact information.');
            }
        }
        catch (error) {
            toast.error(error?.message || 'Failed to auto-fetch contacts.');
        }
        finally {
            setAutoFetchLoading(false);
        }
    };
    const handleSendForApproval = async () => {
        const activeTrackerId = trackerId || sessionTrackerId;
        if (!activeTrackerId) {
            toast.error('Please create an outreach tracker first before sending for approval.');
            return;
        }
        const isSelective = selectedRowIds.size > 0;
        const payload = { tracker_id: activeTrackerId };
        if (isSelective) {
            payload.contact_ids = Array.from(selectedRowIds);
        }
        try {
            setSendingApproval(true);
            await dispatch(sendTrackerForApproval(payload)).unwrap();
            toast.success(isSelective
                ? `Sent ${selectedRowIds.size} selected contact(s) for approval.`
                : 'Sent tracker list for approval successfully.');
            if (isSelective)
                setSelectedRowIds(new Set());
            refreshTrackerData();
        }
        catch (err) {
            toast.error(err || 'Failed to send for approval.');
        }
        finally {
            setSendingApproval(false);
        }
    };
    // Dynamic Options and Filters
    const keywordOptions = useMemo(() => {
        const keywords = new Set();
        currentListDetails.forEach(item => {
            if (item.search_term)
                keywords.add(item.search_term);
        });
        return [
            { value: "All", label: "All" },
            ...Array.from(keywords).map(kw => ({ value: kw, label: kw }))
        ];
    }, [currentListDetails]);
    const domainOptions = useMemo(() => {
        const domains = new Set();
        currentListDetails.forEach(item => {
            if (item.domain)
                domains.add(item.domain);
        });
        return [
            { value: "All", label: "All" },
            ...Array.from(domains).map(dom => ({ value: dom, label: dom }))
        ];
    }, [currentListDetails]);
    const statusOptions = [
        { value: "All", label: "All" },
        { value: "Pending", label: "Pending" },
        { value: "Completed", label: "Completed" },
        { value: "Approved", label: "Approved" },
        { value: "Rejected", label: "Rejected" },
    ];
    const multicontactOptions = [
        { value: "All", label: "All" },
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
    ];
    const filteredRows = useMemo(() => {
        return currentListDetails
            .filter(row => !row.parent_id)
            .filter(row => {
            if (selectedStatusFilter !== 'All' && row.status !== selectedStatusFilter) {
                return false;
            }
            if (selectedKeywordFilter !== 'All' && row.search_term !== selectedKeywordFilter) {
                return false;
            }
            if (selectedDomainFilter !== 'All' && row.domain !== selectedDomainFilter) {
                return false;
            }
            if (selectedMulticontactFilter !== 'All') {
                const hasMulti = currentListDetails.some(c => c.parent_id === row.id);
                if (selectedMulticontactFilter === 'Yes' && !hasMulti)
                    return false;
                if (selectedMulticontactFilter === 'No' && hasMulti)
                    return false;
            }
            return true;
        });
    }, [currentListDetails, selectedStatusFilter, selectedKeywordFilter, selectedDomainFilter, selectedMulticontactFilter]);
    const totalEntries = filteredRows.length;
    const totalPages = Math.ceil(totalEntries / itemsPerPage);
    const paginatedRows = useMemo(() => {
        const start = (page - 1) * itemsPerPage;
        return filteredRows.slice(start, start + itemsPerPage);
    }, [filteredRows, page]);
    // Metric counts computed dynamically from currentListDetails
    const totalContactsCount = currentListDetails.length;
    const bouncedContacts = currentListDetails.filter(c => isBounced(c));
    const bouncedCount = bouncedContacts.length;
    const ineligibleContacts = currentListDetails.filter(c => c.sequences_is_enrolled === 'true' && !isBounced(c));
    const ineligibleCount = ineligibleContacts.length;
    const newContacts = currentListDetails.filter(c => c.sequences_is_enrolled !== 'true' && !c.sequence_enrolled_date && !isBounced(c));
    const newContactsCount = newContacts.length;
    const eligibleContacts = currentListDetails.filter(c => c.sequences_is_enrolled !== 'true' && !!c.sequence_enrolled_date && !isBounced(c));
    const eligibleCount = eligibleContacts.length;
    const exportContactsToXLSX = (contacts, filename) => {
        if (!contacts || contacts.length === 0) {
            toast.warning('No contacts available to export.');
            return;
        }
        let headers = [];
        let dataRows = [];
        if (filename.includes('enrolled-ineligible')) {
            headers = ['Search Term', 'Domain', 'Article', 'First Name', 'Last Name', 'Primary Email', 'Secondary Email', 'Job Title', 'Status', 'Sequence ID', 'Sequence Name', 'Last Enrollment Date'];
            dataRows = contacts.map(c => [
                c.search_term || '',
                c.domain || '',
                c.article || '',
                c.first_name || '',
                c.last_name || '',
                c.primary_email || '',
                c.secondary_email || '',
                c.job_title || '',
                c.status || '',
                c.sequence_id || '',
                c.sequence_name || '',
                c.sequence_enrolled_date || ''
            ]);
        }
        else if (isClient && !hasManageTrackersAccess) {
            headers = ['Search Term', 'Domain', 'Article', 'Status'];
            dataRows = contacts.map(c => [
                c.search_term || '',
                c.domain || '',
                c.article || '',
                c.status || ''
            ]);
        }
        else {
            headers = ['Search Term', 'Domain', 'Article', 'First Name', 'Last Name', 'Primary Email', 'Secondary Email', 'Job Title', 'Status', 'HubSpot Sync Status', 'Bounce Reason'];
            dataRows = contacts.map(c => [
                c.search_term || '',
                c.domain || '',
                c.article || '',
                c.first_name || '',
                c.last_name || '',
                c.primary_email || '',
                c.secondary_email || '',
                c.job_title || '',
                c.status || '',
                c.hubspot_sync_status || '',
                c.bounce_reason || ''
            ]);
        }
        const headerRow = headers.map(h => ({
            v: h,
            t: 's',
            s: { font: { bold: true, color: { rgb: '000000' } }, fill: { fgColor: { rgb: 'D9D9D9' } } }
        }));
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
        // Auto-fit column widths
        ws['!cols'] = headers.map((_, i) => ({
            wch: Math.max(headers[i].length, ...dataRows.map(r => String(r[i] ?? '').length)) + 4
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Outreach Contacts');
        XLSX.writeFile(wb, `${filename}.xlsx`);
        toast.success('Spreadsheet exported successfully.');
    };
    // Latest HubSpot sync date across all contacts
    const latestHubspotSyncDate = useMemo(() => {
        const dates = currentListDetails
            .map(c => c.hubspot_sync_date)
            .filter(Boolean)
            .map((d) => new Date(d).getTime())
            .filter((t) => !isNaN(t));
        if (dates.length === 0)
            return null;
        return new Date(Math.max(...dates)).toISOString();
    }, [currentListDetails]);
    // Status chip styles mapping
    const statusStyles = {
        Pending: { bg: '#FFF4ED', color: '#C2410C', label: 'Pending' },
        'In-Progress': { bg: '#EFF8FF', color: '#026AA2', label: 'In Progress' },
        Approved: { bg: '#ECFDF3', color: '#027A48', label: 'Approved' },
        Rejected: { bg: '#FEF3F2', color: '#B42318', label: 'Rejected' },
    };
    // Table Columns definition — contact detail columns hidden for clients
    const allColumns = [
        { id: "search_term", label: "Search Term", width: "15%", align: "left", sortable: true },
        { id: "article", label: "Article", width: "20%", align: "left", sortable: true },
        { id: "first_name", label: "First Name", width: "12%", align: "left", sortable: true, clientHidden: true },
        { id: "last_name", label: "Last Name", width: "12%", align: "left", sortable: true, clientHidden: true },
        { id: "primary_email", label: "Primary Email", width: "18%", align: "left", sortable: true, clientHidden: true },
        { id: "more", label: "More", width: "8%", align: "left", sortable: false, clientHidden: true },
        { id: "status", label: "Status", width: "10%", align: "left", sortable: true },
        { id: "action", label: "Action", width: "5%", align: "center", sortable: false }
    ];
    const clientColumns = [
        { id: "search_term", label: "Search Term", width: "30%", align: "left", sortable: true },
        { id: "article", label: "Article", width: "50%", align: "left", sortable: true },
        { id: "status", label: "Status", width: "15%", align: "left", sortable: true },
    ];
    const columns = (isClient && !hasManageTrackersAccess) ? clientColumns : allColumns;
    // Cell renderer for CommonTable
    const renderCell = (row, column) => {
        switch (column.id) {
            case "search_term":
                return _jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important' }, children: row.search_term || '-' });
            case "article":
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: {
                        fontSize: '14px !important',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: row.article ? '#026AA2' : 'inherit',
                        cursor: row.article ? 'pointer' : 'default',
                        '&:hover': {
                            textDecoration: row.article ? 'underline' : 'none'
                        }
                    }, onClick: () => row.article && window.open(row.article, '_blank'), children: row.article || '-' }));
            case "first_name":
            case "last_name":
            case "primary_email": {
                const value = row[column.id] || '';
                if (viewOnly) {
                    return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: value ? '#475467' : '#98A2B3', fontStyle: value ? 'normal' : 'italic' }, children: value || (column.id === 'first_name' ? 'First Name' : column.id === 'last_name' ? 'Last Name' : 'Email Address') }));
                }
                const isEditing = editingCell?.rowId === row.id && editingCell?.field === column.id;
                const pendingRowEdits = pendingEdits[row.id];
                const displayValue = (pendingRowEdits && pendingRowEdits[column.id] !== undefined)
                    ? pendingRowEdits[column.id]
                    : value;
                const isPendingEdited = pendingRowEdits && pendingRowEdits[column.id] !== undefined;
                const placeholder = column.id === 'first_name' ? 'First Name' : column.id === 'last_name' ? 'Last Name' : 'Email Address';
                const isOptedOut = column.id === 'primary_email' && optedOutWarnings.has(row.id);
                if (isEditing) {
                    return (_jsx(TextField, { value: tempValue, onChange: (e) => setTempValue(e.target.value), onBlur: async () => {
                            if (column.id === 'primary_email') {
                                if (!tempValue.trim()) {
                                    setOptedOutWarnings(prev => {
                                        const next = new Set(prev);
                                        next.delete(row.id);
                                        return next;
                                    });
                                }
                                else {
                                    const isOut = await checkOptedOut(tempValue.trim());
                                    setOptedOutWarnings(prev => {
                                        const next = new Set(prev);
                                        if (isOut)
                                            next.add(row.id);
                                        else
                                            next.delete(row.id);
                                        return next;
                                    });
                                }
                            }
                            handleSaveInline(row, column.id, tempValue);
                        }, onKeyDown: (e) => {
                            if (e.key === 'Enter')
                                handleSaveInline(row, column.id, tempValue);
                            if (e.key === 'Escape')
                                setEditingCell(null);
                        }, autoFocus: true, size: "small", helperText: column.id === 'primary_email' && (row.hubspot_sync_date || row.hubspot_contact_id)
                            ? '⚠️ Changing email will reset sync data'
                            : undefined, FormHelperTextProps: {
                            sx: { fontSize: '10px !important', color: '#B54708 !important', m: '2px 0 0 0', lineHeight: 1.2 }
                        }, sx: {
                            '& .MuiOutlinedInput-root': {
                                height: '32px',
                                fontSize: '13px',
                                '& fieldset': { borderColor: '#D0D5DD', borderRadius: '6px' },
                                '&:hover fieldset': { borderColor: '#EE2A7B' },
                                '&.Mui-focused fieldset': { borderColor: '#EE2A7B', boxShadow: '0px 0px 0px 4px rgba(238,42,123,0.1)' }
                            }
                        } }));
                }
                const isEnrolled = row.sequences_is_enrolled === 'true' || row.sequences_is_enrolled === true;
                const isEligible = row.hubspot_sync_status === 'Synced' && !isEnrolled;
                const bounced = isBounced(row);
                const enrolledDateStr = row.sequence_enrolled_date ? formatEnrollmentDate(row.sequence_enrolled_date) : '';
                return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', width: '100%' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsxs(Typography, { className: "all-Font-Sizes", onClick: () => {
                                        setEditingCell({ rowId: row.id, field: column.id });
                                        setTempValue(displayValue);
                                    }, sx: {
                                        fontSize: '14px !important',
                                        cursor: 'pointer',
                                        color: isOptedOut ? '#F04438' : isPendingEdited ? '#EE2A7B' : (displayValue ? '#475467' : '#98A2B3'),
                                        fontWeight: isPendingEdited ? 600 : 'normal',
                                        minHeight: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontStyle: displayValue ? 'normal' : 'italic',
                                        textDecoration: isOptedOut ? 'line-through' : 'none',
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }, children: [displayValue || placeholder, isOptedOut && _jsx("span", { style: { marginLeft: '6px', fontSize: '10px', color: '#F04438' }, children: "\u2298" }), isPendingEdited && !isOptedOut && _jsx("span", { style: { marginLeft: '4px', fontSize: '10px', color: '#EE2A7B' }, children: "\u25CF" })] }), column.id === 'primary_email' && (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }, children: bounced ? (_jsx(HoverTooltip, { title: _jsx(Box, { sx: { fontSize: 12, p: 0.5 }, children: "Bounced" }), placement: "top", arrow: true, children: _jsx(Box, { sx: { width: 20, height: 20, borderRadius: '50%', bgcolor: '#FFF4ED', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(MailX, { size: 13, color: "#F79009" }) }) })) : (_jsxs(_Fragment, { children: [isEnrolled && (_jsx(HoverTooltip, { title: _jsxs(Box, { sx: { fontSize: 12, p: 0.5, lineHeight: 1.4 }, children: [_jsx(Box, { sx: { fontWeight: 600 }, children: "Enrolled / Ineligible" }), enrolledDateStr && (_jsxs(Box, { sx: { fontSize: 11, color: '#D0D5DD', mt: 0.25 }, children: ["Last Enrolled: ", enrolledDateStr] }))] }), placement: "top", arrow: true, children: _jsx(Box, { sx: { width: 20, height: 20, borderRadius: '50%', bgcolor: '#FEF3F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(AlertCircle, { size: 13, color: "#F04438" }) }) })), isEligible && (_jsx(HoverTooltip, { title: _jsxs(Box, { sx: { fontSize: 12, p: 0.5, lineHeight: 1.4 }, children: [_jsx(Box, { sx: { fontWeight: 600 }, children: "Unenrolled / Eligible" }), enrolledDateStr && (_jsxs(Box, { sx: { fontSize: 11, color: '#D0D5DD', mt: 0.25 }, children: ["Last Enrolled: ", enrolledDateStr] }))] }), placement: "top", arrow: true, children: _jsx(Box, { sx: { width: 20, height: 20, borderRadius: '50%', bgcolor: '#FDF2F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(CheckCircle2, { size: 13, color: "#EE2A7B" }) }) }))] })) }))] }), isOptedOut && (_jsx(Typography, { sx: { fontSize: '10px', color: '#F04438', lineHeight: 1.2, mt: '2px' }, children: "This contact has opted out. You cannot add them." }))] }));
            }
            case "more": {
                const extraContacts = currentListDetails.filter(c => c.parent_id && String(c.parent_id) === String(row.id));
                const extraCount = extraContacts.length;
                const hasPrimaryEmail = !!row.primary_email?.trim();
                return (_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }, children: [!viewOnly && (_jsx(HoverTooltip, { title: !hasPrimaryEmail ? "Please add primary email first" : "Add multiple contacts", placement: "top", children: _jsx(Box, { onClick: () => hasPrimaryEmail && handleOpenEditDrawer(row), sx: {
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    border: '1px solid #EAECF0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: hasPrimaryEmail ? '#7F56D9' : '#D0D5DD',
                                    bgcolor: hasPrimaryEmail ? '#F9F5FF' : '#F9FAFB',
                                    cursor: hasPrimaryEmail ? 'pointer' : 'not-allowed',
                                    ...(hasPrimaryEmail && {
                                        '&:hover': {
                                            bgcolor: '#F4EBFF',
                                            borderColor: '#D6BBF6'
                                        }
                                    })
                                }, children: _jsx(Plus, { size: 14 }) }) })), extraCount > 0 && (_jsx(HoverTooltip, { title: _jsx(Box, { sx: { p: '4px 2px' }, children: extraContacts.map((c, i) => (_jsxs(Typography, { sx: { fontSize: '12px', lineHeight: 1.6, color: '#fff' }, children: [[c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unknown', " \u2014 ", c.primary_email || 'No email'] }, i))) }), placement: "top", children: _jsx(Box, { sx: {
                                    bgcolor: '#F9F5FF',
                                    color: '#7F56D9',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    px: '8px',
                                    py: '2px',
                                    borderRadius: '8px',
                                    border: '1px solid #E9D7FE',
                                    cursor: 'default'
                                }, onClick: (e) => e.stopPropagation(), children: String(extraCount).padStart(2, '0') }) }))] }));
            }
            case "status":
                const style = statusStyles[row.status] || statusStyles.Pending;
                return (_jsx(Chip, { label: style.label, size: "small", sx: {
                        height: 22,
                        borderRadius: '999px',
                        bgcolor: style.bg,
                        color: style.color,
                        fontSize: '12px',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: '8px' },
                    } }));
            case "action":
                return (_jsxs(Box, { sx: { position: 'relative', display: 'inline-flex', cursor: 'pointer' }, onClick: () => handleOpenNotes(row), children: [_jsx(IconButton, { size: "small", sx: { p: '6px', borderRadius: '6px', '&:hover': { bgcolor: '#F2F4F7' } }, title: row.notes ? "Notes" : "Add Note", children: _jsx("img", { src: "/assets/book.svg", alt: "Note", style: { width: 18, height: 18 } }) }), row.notes && (_jsx(Box, { sx: {
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                bgcolor: '#F04438',
                                border: '1px solid #fff'
                            } }))] }));
            default:
                return null;
        }
    };
    const renderEmptyState = () => (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }, children: _jsx(Box, { component: "img", src: "/assets/outreach-tracker-table.svg", alt: "No data", sx: { maxWidth: '200px', width: '100%', mb: 2 } }) }));
    // Once displayClientName loads from Redux/Context, update the default name (only if user hasn't edited it)
    useEffect(() => {
        if (displayClientName && !userEditedName.current && (!trackerListName || !trackerId)) {
            setTrackerListName(prev => prev || buildDefaultName(displayClientName));
        }
    }, [displayClientName, trackerId]);
    const [searchTerm, setSearchTerm] = useState('');
    const [domain, setDomain] = useState('');
    const [articleLink, setArticleLink] = useState('');
    const [articleVisitor, setArticleVisitor] = useState('');
    const [rank, setRank] = useState('');
    const [notes, setNotes] = useState('');
    const [contacts, setContacts] = useState([
        {
            first_name: '',
            last_name: '',
            primary_email: '',
            secondary_email: '',
            job_title: '',
            status: 'Pending'
        }
    ]);
    const [saving, setSaving] = useState(false);
    const [hubspotAnchor, setHubspotAnchor] = useState(null);
    const [assignToListOpen, setAssignToListOpen] = useState(false);
    const [createPublisherOpen, setCreatePublisherOpen] = useState(false);
    const [searchKeywordOpen, setSearchKeywordOpen] = useState(false);
    const handleBack = () => {
        navigate(ROUTES.accountOverview.path, { state: { restoreTab: 'publisher_tracker' } });
    };
    const addContact = () => {
        setContacts([
            ...contacts,
            {
                first_name: '',
                last_name: '',
                primary_email: '',
                secondary_email: '',
                job_title: '',
                status: 'Pending'
            }
        ]);
    };
    const removeContact = (index) => {
        if (contacts.length === 1) {
            toast.error('At least one contact is required.');
            return;
        }
        setContacts(contacts.filter((_, i) => i !== index));
    };
    const updateContact = (index, field, value) => {
        const updated = [...contacts];
        updated[index] = { ...updated[index], [field]: value };
        setContacts(updated);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validation
        if (!domain.trim()) {
            toast.error('Domain is required.');
            return;
        }
        if (!contacts[0]?.primary_email?.trim()) {
            toast.error('Primary contact email is required.');
            return;
        }
        try {
            setSaving(true);
            const payload = {
                data: {
                    tracker_id: trackerId || sessionTrackerId,
                    name: trackerListName.trim(),
                    search_term: searchTerm.trim(),
                    domain: domain.trim(),
                    article_link: articleLink.trim(),
                    article_visitor: Number(articleVisitor) || 0,
                    rank: Number(rank) || 0,
                    notes: notes.trim(),
                    client_id: selectedClientId || null,
                    contacts: contacts.map(c => ({
                        first_name: c.first_name.trim(),
                        last_name: c.last_name.trim(),
                        primary_email: c.primary_email.trim(),
                        secondary_email: c.secondary_email.trim(),
                        job_title: c.job_title.trim(),
                        status: c.status
                    }))
                }
            };
            const res = await dispatch(manageTrackerList({ action: 'create', payload })).unwrap();
            if (res?.tracker_id && !trackerId && !sessionTrackerId) {
                setSessionTrackerId(res.tracker_id);
            }
            toast.success(trackerId ? 'Outreach tracker updated successfully.' : 'Outreach tracker created successfully.');
            navigate(ROUTES.accountOverview.path, { state: { restoreTab: 'publisher_tracker' } });
        }
        catch (err) {
            toast.error(err || (trackerId ? 'Failed to update tracker.' : 'Failed to create tracker.'));
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(PagesCommonHeader, { title: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: '14px', fontWeight: 600, color: '#667085' }, children: displayClientName || 'Client' }), _jsx(Typography, { sx: { fontSize: '14px', color: '#D0D5DD' }, children: "\u203A" }), editingTrackerName && (!isClient || hasManageTrackersAccess) && !viewOnly ? (_jsx(TextField, { value: tempTrackerName, onChange: (e) => setTempTrackerName(e.target.value), onKeyDown: (e) => {
                                        if (e.key === 'Enter')
                                            handleSaveTrackerName();
                                        if (e.key === 'Escape')
                                            handleCancelTrackerName();
                                    }, autoFocus: true, size: "small", disabled: savingTrackerName, sx: {
                                        '& .MuiOutlinedInput-root': {
                                            height: '28px',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#101828',
                                            '& fieldset': { borderColor: '#EE2A7B', borderRadius: '6px' },
                                            '&:hover fieldset': { borderColor: '#EE2A7B' },
                                            '&.Mui-focused fieldset': { borderColor: '#EE2A7B', boxShadow: '0px 0px 0px 3px rgba(238,42,123,0.1)' },
                                        },
                                        minWidth: '260px',
                                    } })) : (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75 }, children: [_jsx(Typography, { onClick: () => {
                                                if ((isClient && !hasManageTrackersAccess) || viewOnly)
                                                    return;
                                                setTempTrackerName(trackerListName);
                                                setEditingTrackerName(true);
                                            }, sx: {
                                                fontSize: '14px', fontWeight: 600, color: '#101828',
                                                cursor: (isClient && !hasManageTrackersAccess) || viewOnly ? 'default' : 'pointer',
                                                borderBottom: '1px dashed transparent',
                                                ...(!((isClient && !hasManageTrackersAccess) || viewOnly) && { '&:hover': { borderBottomColor: '#EE2A7B', color: '#EE2A7B' } }),
                                            }, children: trackerListName }), !((isClient && !hasManageTrackersAccess) || viewOnly) && (_jsx(HoverTooltip, { title: "Edit tracker name", placement: "top", children: _jsx(IconButton, { size: "small", onClick: () => {
                                                    setTempTrackerName(trackerListName);
                                                    setEditingTrackerName(true);
                                                }, sx: { p: '2px', color: '#98A2B3', '&:hover': { color: '#EE2A7B' } }, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" }), _jsx("path", { d: "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" })] }) }) }))] }))] }), rightSection: _jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: !viewOnly && hasManageTrackersAccess && (_jsxs(_Fragment, { children: [_jsx(IconButton, { onClick: (e) => setHubspotAnchor(e.currentTarget), sx: {
                                            width: 36,
                                            height: 36,
                                            borderRadius: '8px',
                                            border: '1px solid #D0D5DD',
                                            p: '6px',
                                            '&:hover': { bgcolor: '#F9FAFB' },
                                        }, children: _jsx(Box, { component: "img", src: "/assets/hubspot.svg", alt: "HubSpot", sx: { width: 20, height: 20 } }) }), _jsxs(Menu, { anchorEl: hubspotAnchor, open: Boolean(hubspotAnchor), onClose: () => setHubspotAnchor(null), anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, sx: {
                                            zIndex: 1500,
                                            '& .MuiPaper-root': {
                                                borderRadius: '8px',
                                                boxShadow: '0px 4px 16px rgba(16,24,40,0.12)',
                                                border: '1px solid #EAECF0',
                                                minWidth: '200px',
                                                mt: 0.5,
                                            }
                                        }, children: [_jsxs(MenuItem, { onClick: handleContactSync, disabled: syncLoading || savingPending, sx: { gap: 1.5, py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(RefreshCw, { size: 16 }), "Contact sync"] }), _jsxs(MenuItem, { onClick: () => { setHubspotAnchor(null); setAssignToListOpen(true); }, sx: { gap: 1.5, py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(UserCheck, { size: 16 }), "Contacts Assign To List"] })] }), Boolean(hubspotAnchor) && (_jsx(Portal, { children: _jsx(Box, { onClick: () => setHubspotAnchor(null), sx: {
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                width: '100vw',
                                                height: '100vh',
                                                bgcolor: 'rgba(0, 0, 0, 0.15)',
                                                zIndex: 1400,
                                            } }) })), _jsx(Button, { variant: "outlined", startIcon: _jsx(Box, { component: "img", src: "/assets/searchkeyword.svg", alt: "", sx: { width: 16, height: 16 } }), onClick: () => setSearchKeywordOpen(true), sx: {
                                            height: '36px',
                                            borderColor: '#FFC8E9',
                                            color: '#FF008E',
                                            backgroundColor: "#FFFAFD",
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            borderRadius: '8px',
                                            bgcolor: '#fff',
                                            '&:hover': { borderColor: '#D91F6A', color: '#D91F6A', bgcolor: '#FFF5F9' },
                                        }, children: "Search Keyword" }), _jsx(Button, { variant: "contained", startIcon: _jsx(Plus, { size: 16 }), onClick: () => setCreatePublisherOpen(true), sx: {
                                            height: '36px',
                                            bgcolor: '#EE2A7B',
                                            color: '#fff',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            borderRadius: '8px',
                                            '&:hover': { bgcolor: '#D91F6A' },
                                        }, children: "Create Publisher" })] })) }) }), !isClient && (_jsx(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#fff',
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            p: 1.5,
                        }, children: _jsx(LastUpdatedInfo, { updatedAt: latestHubspotSyncDate, onRefresh: handleContactSync, loading: loading || syncLoading, showIcon: true }) })), !isClient && _jsxs(Box, { sx: {
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: 2,
                            width: '100%',
                        }, children: [_jsx(MetricCard, { title: "Total Contacts", value: totalContactsCount, icon: _jsx(BookOpen, { size: 16 }), tooltipText: "Total number of contacts added by the outreach operator. It includes unique Primary & Secondary Emails of both main contact and multicontacts.", sx: { p: 2, height: '92px', justifyContent: 'space-between' } }), _jsx(MetricCard, { title: "Bounced Contacts", value: bouncedCount, icon: _jsx(TrendingDown, { size: 16 }), tooltipText: "Contacts that have been synced to HubSpot and bounced in a sequence.", rightAction: _jsx(IconButton, { size: "small", sx: { p: 0, color: '#98A2B3', '&:hover': { color: '#475467' } }, onClick: () => exportContactsToXLSX(bouncedContacts, 'bounced-contacts'), children: _jsx(Download, { size: 16 }) }), sx: { p: 2, height: '92px', justifyContent: 'space-between' } }), _jsx(MetricCard, { title: "Enrolled/Ineligible", value: ineligibleCount, icon: _jsx(Inbox, { size: 16 }), tooltipText: "Contacts that have been synced to HubSpot and enrolled in a sequence.", rightAction: _jsx(IconButton, { size: "small", sx: { p: 0, color: '#98A2B3', '&:hover': { color: '#475467' } }, onClick: () => exportContactsToXLSX(ineligibleContacts, 'enrolled-ineligible-contacts'), children: _jsx(Download, { size: 16 }) }), sx: { p: 2, height: '92px', justifyContent: 'space-between' } }), _jsx(MetricCard, { title: "Unenrolled/Eligible", value: eligibleCount, icon: _jsx(Radio, { size: 16 }), tooltipText: "Contacts previously enrolled in a sequence that are currently not enrolled.", rightAction: _jsx(IconButton, { size: "small", sx: { p: 0, color: '#98A2B3', '&:hover': { color: '#475467' } }, onClick: () => exportContactsToXLSX(eligibleContacts, 'unenrolled-eligible-contacts'), children: _jsx(Download, { size: 16 }) }), sx: { p: 2, height: '92px', justifyContent: 'space-between' } }), _jsx(MetricCard, { title: "New/Eligible Contacts", value: newContactsCount, icon: _jsx(User, { size: 16 }), tooltipText: "Contacts that have never been enrolled in any sequence.", rightAction: _jsx(IconButton, { size: "small", sx: { p: 0, color: '#98A2B3', '&:hover': { color: '#475467' } }, onClick: () => exportContactsToXLSX(newContacts, 'new-eligible-contacts'), children: _jsx(Download, { size: 16 }) }), sx: { p: 2, height: '92px', justifyContent: 'space-between' } })] }), _jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #EAECF0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }, children: [_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 2, borderBottom: '1px solid #EAECF0', flexWrap: 'nowrap', gap: 2 }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }, children: [!isClient && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #EAECF0', borderRadius: '8px', px: 1.5, py: 0.5, height: '36px' }, children: [_jsx(Typography, { sx: { fontSize: '13px', color: '#667085', fontWeight: 500 }, children: "View All" }), _jsx(IOSSwitch, { checked: contactNotAddedFilter, onChange: (e) => {
                                                        setContactNotAddedFilter(e.target.checked);
                                                        setPage(1);
                                                    } }), _jsx(Typography, { sx: { fontSize: '13px', color: '#667085', fontWeight: 500 }, children: "No Contact" })] })), _jsx(Dropdown, { value: selectedStatusFilter, onChange: (v) => {
                                                setSelectedStatusFilter(v);
                                                setPage(1);
                                            }, options: statusOptions, displayValue: _jsxs("span", { className: "all-Font-Sizes", children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Status :" }), " ", _jsx("span", { style: { color: "#667085" }, children: selectedStatusFilter })] }), size: "sm", fullWidth: false, className: "header-dropdown" }), _jsx(Dropdown, { value: selectedKeywordFilter, onChange: (v) => {
                                                setSelectedKeywordFilter(v);
                                                setPage(1);
                                            }, options: keywordOptions, displayValue: _jsxs("span", { className: "all-Font-Sizes", children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Keyword :" }), " ", _jsx("span", { style: { color: "#667085" }, children: selectedKeywordFilter })] }), size: "sm", fullWidth: false, className: "header-dropdown", searchable: true }), _jsx(Dropdown, { value: selectedDomainFilter, onChange: (v) => {
                                                setSelectedDomainFilter(v);
                                                setPage(1);
                                            }, options: domainOptions, displayValue: _jsxs("span", { className: "all-Font-Sizes", children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Domain :" }), " ", _jsx("span", { style: { color: "#667085" }, children: selectedDomainFilter })] }), size: "sm", fullWidth: false, className: "header-dropdown", searchable: true }), _jsx(Dropdown, { value: selectedMulticontactFilter, onChange: (v) => {
                                                setSelectedMulticontactFilter(v);
                                                setPage(1);
                                            }, options: multicontactOptions, displayValue: _jsxs("span", { className: "all-Font-Sizes", children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Multicontact :" }), " ", _jsx("span", { style: { color: "#667085" }, children: selectedMulticontactFilter })] }), size: "sm", fullWidth: false, className: "header-dropdown" }), !isClient && !viewOnly && hasManageTrackersAccess && allContactsApproved && (_jsx(Button, { variant: "outlined", startIcon: autoFetchLoading ? _jsx(RefreshCw, { size: 16, className: "spin" }) : _jsx(Zap, { size: 16 }), onClick: handleBulkAutoFetch, disabled: autoFetchLoading || !(trackerId || sessionTrackerId), sx: {
                                                height: '36px',
                                                borderColor: '#D0D5DD',
                                                color: '#344054',
                                                backgroundColor: '#fff',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                borderRadius: '8px',
                                                '&:hover': { borderColor: '#D0D5DD', bgcolor: '#F9FAFB' },
                                                '&.Mui-disabled': { borderColor: '#EAECF0', color: '#98A2B3', backgroundColor: '#F9FAFB' }
                                            }, children: autoFetchLoading ? 'Fetching...' : 'Auto-Fetch Contact' })), !isClient && !viewOnly && hasClientApprovalAccess && (_jsx(Button, { variant: "outlined", startIcon: sendingApproval ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : _jsx(Send, { size: 16 }), onClick: handleSendForApproval, disabled: sendingApproval || !(trackerId || sessionTrackerId), sx: {
                                                height: '36px',
                                                borderColor: '#D0D5DD',
                                                color: '#344054',
                                                backgroundColor: '#fff',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                borderRadius: '8px',
                                                '&:hover': { borderColor: '#D0D5DD', bgcolor: '#F9FAFB' },
                                                '&.Mui-disabled': { borderColor: '#EAECF0', color: '#98A2B3', backgroundColor: '#F9FAFB' }
                                            }, children: sendingApproval ? 'Sending...' : 'Send For Approval' })), _jsx(ExportButton, { onClick: () => {
                                                const rowsToExport = filteredRows.length > 0 ? filteredRows : currentListDetails;
                                                const cleanName = (trackerListName || 'outreach-tracker').toLowerCase().replace(/[^a-z0-9]+/g, '-');
                                                exportContactsToXLSX(rowsToExport, cleanName);
                                            } })] }) }), _jsx(Box, { sx: { position: 'relative' }, children: _jsx(CommonTable, { columns: columns, rows: paginatedRows, renderCell: renderCell, isLoading: loading, isFirstLoad: loading && paginatedRows.length === 0, renderEmpty: renderEmptyState, enableSelection: !viewOnly, selectedRows: selectedRowIds, onSelectionChange: setSelectedRowIds, stickyHeader: true, maxHeight: "calc(100vh - 315px)", expandTrigger: "none" }) }), totalEntries > 0 && (_jsx(Box, { sx: { p: '12px 16px', borderTop: '1px solid #EAECF0' }, children: _jsx(Pagination, { currentPage: page, totalPages: totalPages, totalEntries: totalEntries, itemsPerPage: itemsPerPage, onPageChange: setPage }) }))] })] }), _jsx(CreatePublisherDrawer, { open: createPublisherOpen, onClose: () => setCreatePublisherOpen(false), trackerId: trackerId || sessionTrackerId, clientId: selectedClientId, onCreated: (newTrackerId) => {
                    const finalId = newTrackerId || trackerId || sessionTrackerId;
                    if (newTrackerId) {
                        setSessionTrackerId(newTrackerId);
                        setSearchParams(prev => {
                            const next = new URLSearchParams(prev);
                            next.set('id', newTrackerId);
                            return next;
                        }, { replace: true });
                    }
                    if (finalId) {
                        dispatch(fetchTrackerDetails({ id: finalId, noContact: contactNotAddedFilter }));
                        dispatch(fetchTrackerLists());
                    }
                } }), _jsx(SearchKeywordDrawer, { open: searchKeywordOpen, onClose: () => setSearchKeywordOpen(false), trackerId: trackerId || sessionTrackerId, clientId: selectedClientId, onCreated: (newTrackerId) => {
                    const finalId = newTrackerId || trackerId || sessionTrackerId;
                    if (newTrackerId) {
                        setSessionTrackerId(newTrackerId);
                        setSearchParams(prev => {
                            const next = new URLSearchParams(prev);
                            next.set('id', newTrackerId);
                            return next;
                        }, { replace: true });
                    }
                    if (finalId) {
                        dispatch(fetchTrackerDetails({ id: finalId, noContact: contactNotAddedFilter }));
                        dispatch(fetchTrackerLists());
                    }
                } }), _jsx(EditTrackerDrawer, { open: editTrackerDrawerOpen, onClose: () => setEditTrackerDrawerOpen(false), selectedRow: selectedTrackerRow, currentListDetails: currentListDetails, statusStyles: statusStyles, onSaveSuccess: refreshTrackerData }), _jsx(NotesModal, { open: notesOpen, onClose: () => setNotesOpen(false), initialNotes: notesValue, onSave: handleSaveNotes, actionLoading: notesSaving }), _jsx(ConfirmActionModal, { open: deleteModalOpen, onClose: () => setDeleteModalOpen(false), onConfirm: handleBulkDelete, title: "Delete Outreach Trackers", description: `Are you sure you want to delete the ${selectedRowIds.size} selected outreach tracker contacts? This action is permanent and cannot be undone.`, confirmText: "Delete", cancelText: "Cancel", loading: savingPending }), _jsx(AssignToListModal, { open: assignToListOpen, onClose: () => setAssignToListOpen(false), trackerId: trackerId || sessionTrackerId, selectedContactIds: selectedRowIds.size > 0
                    ? Array.from(selectedRowIds).filter(id => {
                        const c = currentListDetails.find((c) => c.id === id);
                        return isApproved(c) && !isBounced(c) && !isIneligible(c) && !!c?.primary_email?.trim() && !!c?.hubspot_sync_date;
                    })
                    : currentListDetails
                        .filter((c) => isApproved(c) && !isBounced(c) && !isIneligible(c) && !!c?.primary_email?.trim() && !!c?.hubspot_sync_date)
                        .map((c) => c.id), onSuccess: async () => {
                    setSyncLoading(true);
                    try {
                        await dispatch(fetchTrackerDetails({ id: trackerId || sessionTrackerId, noContact: contactNotAddedFilter })).unwrap();
                    }
                    catch { }
                    setSyncLoading(false);
                } }), _jsx(FloatingSaveBar, { isVisible: editingTrackerName && !isClient && !viewOnly, count: 1, message: "Unsaved tracker name change", countLabel: "Unsaved Change", onReset: handleCancelTrackerName, onSave: handleSaveTrackerName, updating: savingTrackerName, saveLabel: "Save Name" }), _jsx(FloatingSaveBar, { isVisible: !viewOnly && !editingTrackerName && (Object.keys(pendingEdits).length > 0 || hasActiveCellChange), count: totalUnsavedCount, message: "Unsaved inline cell changes in tracker list", countLabel: totalUnsavedCount > 1 ? "Unsaved Changes" : "Unsaved Change", onReset: () => { setPendingEdits({}); setEditingCell(null); setOptedOutWarnings(new Set()); }, onSave: handleSaveAllPendingEdits, updating: savingPending, saveLabel: "Save Changes" }), _jsx(FloatingSaveBar, { isVisible: !viewOnly && !editingTrackerName && selectedRowIds.size > 0 && Object.keys(pendingEdits).length === 0 && !hasActiveCellChange, count: selectedRowIds.size, message: "Outreach contacts selected for bulk action", countLabel: "Selected Rows", details: selectedArticleNames, onReset: () => { setSelectedRowIds(new Set()); setStagedStatus(''); }, children: _jsxs(Box, { sx: { display: 'flex', gap: 1.5, alignItems: 'center' }, children: [(hasManageTrackersAccess || hasClientApprovalAccess) && (_jsx(Dropdown, { usePortal: true, value: stagedStatus, onChange: (v) => setStagedStatus(v), options: [
                                { value: 'Pending', label: 'Pending' },
                                { value: 'Approved', label: 'Approved' },
                                { value: 'Rejected', label: 'Rejected' },
                            ], displayValue: _jsx("span", { className: "all-Font-Sizes", style: { color: '#475467', fontWeight: 600 }, children: stagedStatus || 'Change Status' }), size: "sm", fullWidth: false, className: "bulk-action-dropdown" })), stagedStatus && (hasManageTrackersAccess || hasClientApprovalAccess) && (_jsx(Button, { onClick: () => {
                                handleBulkStatusChange(stagedStatus);
                                setStagedStatus('');
                            }, disabled: savingPending, sx: {
                                textTransform: 'none',
                                bgcolor: '#EE2A7B',
                                color: '#fff',
                                fontWeight: 600,
                                px: 2,
                                height: '36px',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: '#D91F6A' },
                                '&.Mui-disabled': { bgcolor: '#f9a8d4', color: '#fff' }
                            }, children: "Save" })), !isClient && hasManageTrackersAccess && (_jsx(Button, { onClick: handleBulkHubspotSync, disabled: savingPending, startIcon: _jsx(Box, { component: "img", src: "/assets/hubspot.svg", alt: "", sx: { width: 16, height: 16 } }), sx: {
                                textTransform: "none", bgcolor: '#fff', color: '#344054', border: '1px solid #D0D5DD', fontWeight: 600, px: 2, height: "36px",
                                borderRadius: "8px", "&:hover": { bgcolor: '#f9fafb' },
                                "&.Mui-disabled": { bgcolor: "#f2f4f7", color: "#98a2b3" }
                            }, children: "Sync HubSpot" })), !isClient && hasManageTrackersAccess && (_jsx(Button, { onClick: () => setDeleteModalOpen(true), disabled: savingPending, sx: {
                                textTransform: "none", bgcolor: '#D92D20', color: '#fff', fontWeight: 600, px: 2, height: "36px",
                                borderRadius: "8px", boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                                "&:hover": { bgcolor: '#B42318' },
                                "&.Mui-disabled": { bgcolor: "#fca5a5", color: "#fff" }
                            }, children: "Delete" }))] }) })] }));
}
